/**
 * i18n helpers для локализации полей моделей в magic geo-стеке (EN+KA).
 *
 * Стратегия Этапа 2:
 *   - В БД для каждой переводимой модели есть пары полей: nameEn/nameKa, descriptionEn/descriptionKa и т.п.
 *   - Старые поля name/description/title — nullable (deprecated, но не удалены чтобы не ломать legacy-код).
 *   - Loaders в apps/web принимают `locale: Locale` и через `localize*()` возвращают модели,
 *     где старое поле name заменено на локализованную версию (nameEn если locale='en' и т.д.).
 *   - UI-код (`{product.name}`) НЕ меняется — он продолжает читать `.name`, но видит локализованное.
 *   - В apps/admin loaders НЕ локализуют — там UI редактирует пары полей напрямую.
 */

export type Locale = 'en' | 'ka';

export const SUPPORTED_LOCALES: readonly Locale[] = ['en', 'ka'] as const;
export const DEFAULT_LOCALE: Locale = 'en';

/**
 * Выбирает локализованную строку с фоллбэком на цепочку:
 *   1. <field><Locale>  (target locale)
 *   2. <field><En>      (другая локаль)
 *   3. <field>          (старое legacy-поле)
 *   4. fallback аргумент (если передан)
 *   5. ''               (никогда не возвращает null/undefined)
 *
 * @example
 *   pickLocalized(product, 'name', 'en') // → product.nameEn ?? product.nameKa ?? product.name ?? ''
 *   pickLocalized(product, 'description', 'ka', 'нет описания')
 */
export function pickLocalized<
    M extends Record<string, unknown>,
    K extends string,
>(model: M, field: K, locale: Locale, fallback = ''): string {
    const targetKey = `${field}${capitalize(locale)}` as K;
    const otherKey = `${field}${locale === 'en' ? 'Ka' : 'En'}` as K;

    const target = model[targetKey];
    if (typeof target === 'string' && target.length > 0) return target;

    const other = model[otherKey];
    if (typeof other === 'string' && other.length > 0) return other;

    const legacy = model[field as unknown as keyof M];
    if (typeof legacy === 'string' && legacy.length > 0) return legacy;

    return fallback;
}

/**
 * То же что pickLocalized, но для nullable полей возвращает null а не ''.
 * Подходит для description/metadata где пустая строка != «нет значения».
 */
export function pickLocalizedNullable<
    M extends Record<string, unknown>,
    K extends string,
>(model: M, field: K, locale: Locale): string | null {
    const targetKey = `${field}${capitalize(locale)}` as K;
    const otherKey = `${field}${locale === 'en' ? 'Ka' : 'En'}` as K;

    const target = model[targetKey];
    if (typeof target === 'string' && target.length > 0) return target;

    const other = model[otherKey];
    if (typeof other === 'string' && other.length > 0) return other;

    const legacy = model[field as unknown as keyof M];
    if (typeof legacy === 'string' && legacy.length > 0) return legacy;

    return null;
}

/**
 * Локализаторы конкретных моделей. Принимают raw-модель из Prisma + локаль.
 * Возвращают новую модель где старые поля name/title/description/metadata*
 * замещены локализованными значениями. Multilang-поля (`*En`, `*Ka`)
 * сохраняются в результате — иногда они нужны (например, hreflang теги).
 */

type ProductLike = {
    name: string | null;
    nameEn: string;
    nameKa: string;
    description?: string | null;
    descriptionEn?: string | null;
    descriptionKa?: string | null;
};

export function localizeProduct<P extends ProductLike>(product: P, locale: Locale): P {
    return {
        ...product,
        name: pickLocalized(product, 'name', locale, product.nameEn ?? ''),
        description: pickLocalizedNullable(product, 'description', locale),
    };
}

type BrandLike = {
    name: string | null;
    nameEn: string;
    nameKa: string;
};

export function localizeBrand<B extends BrandLike>(brand: B, locale: Locale): B {
    return { ...brand, name: pickLocalized(brand, 'name', locale, brand.nameEn ?? '') };
}

type CategoryLike = {
    name: string | null;
    nameEn: string;
    nameKa: string;
    metadataTitle?: string | null;
    metadataTitleEn?: string | null;
    metadataTitleKa?: string | null;
    metadataDescription?: string | null;
    metadataDescriptionEn?: string | null;
    metadataDescriptionKa?: string | null;
    metadataKeywords?: string[];
    metadataKeywordsEn?: string[];
    metadataKeywordsKa?: string[];
    metadataPageDescription?: string | null;
    metadataPageDescriptionEn?: string | null;
    metadataPageDescriptionKa?: string | null;
};

export function localizeCategory<C extends CategoryLike>(category: C, locale: Locale): C {
    return {
        ...category,
        name: pickLocalized(category, 'name', locale, category.nameEn ?? ''),
        metadataTitle: pickLocalizedNullable(category, 'metadataTitle', locale),
        metadataDescription: pickLocalizedNullable(category, 'metadataDescription', locale),
        metadataKeywords: pickLocalizedKeywords(category, locale),
        metadataPageDescription: pickLocalizedNullable(category, 'metadataPageDescription', locale),
    };
}

type HomePageLike = {
    title: string | null;
    titleEn: string;
    titleKa: string;
    description?: unknown; // JSON в БД — PortableText блоки
    descriptionEn?: unknown;
    descriptionKa?: unknown;
};

export function localizeHomePage<H extends HomePageLike>(page: H, locale: Locale): H {
    const localizedDesc = locale === 'en' ? page.descriptionEn : page.descriptionKa;
    return {
        ...page,
        title: pickLocalized(page, 'title', locale, page.titleEn ?? ''),
        description: localizedDesc ?? page.description ?? null,
    };
}

type SelectionListLike = {
    title: string | null;
    titleEn: string;
    titleKa: string;
    description?: string | null;
    descriptionEn?: string | null;
    descriptionKa?: string | null;
    metadataTitle?: string | null;
    metadataTitleEn?: string | null;
    metadataTitleKa?: string | null;
    metadataDescription?: string | null;
    metadataDescriptionEn?: string | null;
    metadataDescriptionKa?: string | null;
    metadataKeywords?: string[];
    metadataKeywordsEn?: string[];
    metadataKeywordsKa?: string[];
    metadataPageDescription?: string | null;
    metadataPageDescriptionEn?: string | null;
    metadataPageDescriptionKa?: string | null;
};

export function localizeSelectionList<L extends SelectionListLike>(list: L, locale: Locale): L {
    return {
        ...list,
        title: pickLocalized(list, 'title', locale, list.titleEn ?? ''),
        description: pickLocalizedNullable(list, 'description', locale),
        metadataTitle: pickLocalizedNullable(list, 'metadataTitle', locale),
        metadataDescription: pickLocalizedNullable(list, 'metadataDescription', locale),
        metadataKeywords: pickLocalizedKeywords(list, locale),
        metadataPageDescription: pickLocalizedNullable(list, 'metadataPageDescription', locale),
    };
}

type SeoMetadataLike = {
    title: string | null;
    titleEn: string;
    titleKa: string;
    description?: string | null;
    descriptionEn?: string | null;
    descriptionKa?: string | null;
    keywords?: string | null;
    keywordsEn?: string | null;
    keywordsKa?: string | null;
    pageDescription?: string | null;
    pageDescriptionEn?: string | null;
    pageDescriptionKa?: string | null;
};

export function localizeSeo<S extends SeoMetadataLike>(seo: S, locale: Locale): S {
    return {
        ...seo,
        title: pickLocalized(seo, 'title', locale, seo.titleEn ?? ''),
        description: pickLocalizedNullable(seo, 'description', locale),
        keywords: pickLocalizedNullable(seo, 'keywords', locale),
        pageDescription: pickLocalizedNullable(seo, 'pageDescription', locale),
    };
}

// ---------- internals ----------

function capitalize<S extends string>(s: S): Capitalize<S> {
    return (s.charAt(0).toUpperCase() + s.slice(1)) as Capitalize<S>;
}

function pickLocalizedKeywords<
    M extends Record<string, unknown>,
>(model: M, locale: Locale): string[] {
    const targetKey = `metadataKeywords${capitalize(locale)}`;
    const target = model[targetKey];
    if (Array.isArray(target) && target.length > 0) return target as string[];

    const otherKey = `metadataKeywords${locale === 'en' ? 'Ka' : 'En'}`;
    const other = model[otherKey];
    if (Array.isArray(other) && other.length > 0) return other as string[];

    const legacy = model['metadataKeywords'];
    if (Array.isArray(legacy)) return legacy as string[];

    return [];
}
